/**
 * Axios API client for the WorkRight backend.
 *
 * Contract (see backend/internal/handlers + middleware):
 *  - Auth is COOKIE-ONLY. The backend sets three cookies:
 *      wr_access  (httpOnly)            — JWT access token
 *      wr_refresh (httpOnly, /api/v1/auth scope) — rotating refresh token
 *      wr_csrf    (readable)            — CSRF double-submit token
 *    We must NEVER read/write a JWT in localStorage/sessionStorage.
 *  - Every request sends cookies (`withCredentials: true`).
 *  - State-changing requests (POST/PUT/PATCH/DELETE) must echo the
 *    readable wr_csrf cookie value in the `X-CSRF-Token` header
 *    (middleware/csrf.go double-submit check).
 *  - On 401 we attempt exactly ONE refresh (POST /api/v1/auth/refresh,
 *    which rotates the refresh token) and retry the original request.
 *    A second failure redirects to /login. No infinite loops.
 */

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import type { ApiError } from "@/types/auth";
import { safeNext } from "@/lib/auth/safe-next";

// Cookie/header names — must match backend/internal/middleware/auth.go.
export const CSRF_COOKIE = "wr_csrf";
export const CSRF_HEADER = "X-CSRF-Token";

// In-memory CSRF token store. On a same-origin deploy (production:
// app.workright.in + api.workright.in sharing `.workright.in` Domain
// cookie) the readable `wr_csrf` cookie is authoritative — the store
// stays empty and we fall through to the cookie path. On a cross-
// origin deploy (current staging: staging.workright.in calling
// backend-staging-eedb.up.railway.app), the backend's host-only cookie
// is unreadable from the frontend's `document.cookie`, so we stash
// the `csrf_token` from the login/register response body here and the
// request interceptor uses it as the fallback. Persisted to
// sessionStorage so a page refresh doesn't lose the value mid-session.
const CSRF_STORAGE_KEY = "wr_csrf_memory";
let csrfMemory: string | null = null;

export function setCSRFToken(token: string | null | undefined): void {
  if (!token) return;
  csrfMemory = token;
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.setItem(CSRF_STORAGE_KEY, token);
    } catch {
      // sessionStorage may throw in incognito / quota-exceeded; the
      // in-memory copy still works for the current page lifetime.
    }
  }
}

export function clearCSRFToken(): void {
  csrfMemory = null;
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.removeItem(CSRF_STORAGE_KEY);
    } catch {
      // best effort
    }
  }
}

function readCSRFFromStore(): string | null {
  if (csrfMemory) return csrfMemory;
  if (typeof window === "undefined") return null;
  try {
    csrfMemory = window.sessionStorage.getItem(CSRF_STORAGE_KEY);
    return csrfMemory;
  } catch {
    return null;
  }
}

// API origin. The backend mounts everything under /api/v1; we keep that
// prefix in each call site so paths read like the router table.
//
// Exported because some surfaces need an absolute URL (CSV downloads
// triggered by an `<a href download>` anchor — the browser resolves
// relative hrefs against the current origin, which is the frontend,
// not the backend, so the CSV button 404s in any deploy where the two
// services live at different origins. See G_ACC8 HIGH-1 audit fix.)
// Migration note (2026-06-01, Next.js → Vite): the Next.js sibling at
// frontend/src/lib/api/client.ts reads
// `process.env.NEXT_PUBLIC_API_URL` — baked at next build time. Vite's
// equivalent is `import.meta.env.VITE_API_URL` (bundle-baked too).
//
// Dev fallback (2026-06-02 fix): empty string, so axios issues
// SAME-ORIGIN requests. The Vite dev server's /api/* proxy
// (vite.config.ts) then forwards to localhost:8090 transparently.
// This dodges CORS preflight entirely in dev — the cross-origin
// hard-coded fallback we had before was getting blocked by the
// backend's allow-list (which doesn't include localhost:3001) and
// every browser-side API call from the dev SPA failed at the
// preflight stage. The Audit 1 walkthrough was contaminated by this.
//
// In production builds, VITE_API_URL is always set (baked at build
// time by the CF Pages build env), so this fallback never runs.
export const API_BASE_URL =
  import.meta.env.VITE_API_URL?.replace(/\/$/, "") ?? "";

const REFRESH_PATH = "/api/v1/auth/refresh";
const UNSAFE_METHODS = new Set(["post", "put", "patch", "delete"]);

/** Read a readable (non-httpOnly) cookie by name. */
export function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// --- Request interceptor: inject CSRF header on unsafe methods. --------
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const method = (config.method ?? "get").toLowerCase();
  if (UNSAFE_METHODS.has(method)) {
    // Same-origin deploy: read the authoritative wr_csrf cookie.
    // Cross-origin deploy (staging): fall back to the in-memory token
    // captured from the login/register response body via setCSRFToken().
    const token = readCookie(CSRF_COOKIE) ?? readCSRFFromStore();
    if (token) {
      config.headers.set(CSRF_HEADER, token);
    }
  }
  return config;
});

// --- Response interceptor: single refresh-then-retry on 401. ----------
//
// We coalesce concurrent 401s onto a single in-flight refresh so we
// never hammer the rotating-refresh endpoint. A burst of parallel
// requests must NOT each rotate the refresh token: the backend treats a
// reuse of an already-rotated token as theft (refresh_reuse), revokes
// every session, and 401s. So coalescing is a security property, not
// just an optimisation.
//
// Coalescing is gated on an epoch counter, not merely a nullable
// in-flight promise. A 401'd request captures the epoch it observed.
// After awaiting the shared refresh it retries once against the new
// cookie. If that retry also 401s, the epoch has moved on, so the
// request does NOT start a *second* refresh (which would rotate the
// token again and trip refresh_reuse) — it just fails out to /login.
// Only a *failed* refresh re-arms the gate so a genuinely new session
// can refresh later.

export type RetriableConfig = AxiosRequestConfig & {
  _retried?: boolean;
  /**
   * Set by the mount-time /me probe. On 401 the interceptor neither
   * refreshes nor redirects — it rejects quietly so AuthProvider can
   * set user=null for an anonymous visitor (e.g. on /register, /forgot)
   * instead of bouncing them to /login?next=/register.
   */
  _suppressAuthRedirect?: boolean;
};

let refreshInFlight: Promise<void> | null = null;
// Bumped every time a refresh attempt settles. A request remembers the
// epoch it saw at 401 time; if the epoch advanced while it waited, the
// shared refresh already ran (success or failure) and it must not kick
// off another one.
let refreshEpoch = 0;

// Idempotent hard-redirect guard: at most one window.location.assign
// per logical auth failure, even though many coalesced requests reject
// at once. Never reset within a tick — the page is navigating away.
let redirecting = false;

/**
 * Single owner of the post-auth-failure hard navigation. `reason`, when
 * given, is surfaced on the login page (e.g. session_revoked after a
 * refresh_reuse). The `next` target is derived from the *current*
 * same-origin location (browser-provided, not request input) and run
 * through safeNext()/encodeURIComponent, so it cannot be steered to an
 * off-origin URL — see client.ts open-redirect note below.
 */
function redirectToLogin(reason?: "session_revoked") {
  if (typeof window === "undefined") return;
  if (redirecting) return;
  const { pathname, search } = window.location;
  if (pathname.startsWith("/login")) {
    redirecting = true;
    if (reason) window.location.assign(`/login?reason=${reason}`);
    return;
  }
  redirecting = true;
  // pathname+search is the browser's own current URL, never a value
  // taken from a response/request. safeNext() still defends against a
  // crafted current path (e.g. //evil if somehow navigated there) and
  // encodeURIComponent keeps it a single query param value.
  const next = encodeURIComponent(safeNext(pathname + search));
  const reasonQ = reason ? `&reason=${reason}` : "";
  window.location.assign(`/login?next=${next}${reasonQ}`);
}

function isRefreshReuse(error: AxiosError<ApiError>): boolean {
  return error.response?.data?.error === "refresh_reuse";
}

async function runRefresh(): Promise<void> {
  // The refresh cookie is httpOnly and scoped to /api/v1/auth; the
  // browser attaches it automatically because of withCredentials.
  // _retried marks it so a 401 here can't recurse into another refresh.
  await api.post(REFRESH_PATH, null, { _retried: true } as RetriableConfig);
}

/**
 * Coalesced, epoch-gated refresh.
 *
 * `seenEpoch` is the refresh generation the calling request's stale
 * cookie belonged to when it 401'd. Behaviour:
 *  - a refresh is already in flight  → await it (pure coalescing).
 *  - no refresh in flight AND the epoch hasn't moved since this request
 *    saw its 401 → start exactly one refresh (rotates the token once).
 *  - no refresh in flight BUT the epoch already advanced → a peer's
 *    refresh completed after our cookie went stale; do NOT start a
 *    second rotation (would trip backend refresh_reuse). Resolve so the
 *    caller retries once against the cookie the peer already obtained.
 * Every settle (success or failure) advances the epoch and clears the
 * in-flight slot, so a failed refresh re-arms the gate for a future
 * legitimately-new session.
 */
async function coalescedRefresh(seenEpoch: number): Promise<void> {
  const inFlight = refreshInFlight;
  if (inFlight !== null) {
    await inFlight;
    return;
  }
  if (refreshEpoch !== seenEpoch) {
    // A newer cookie already exists — skip refresh, let caller retry.
    return;
  }
  const started = runRefresh()
    .finally(() => {
      // Advance the epoch on EVERY settle. On success this blocks a
      // second token-rotating refresh from stale-cookie 401s; on
      // failure it re-arms the gate for a future new session.
      refreshEpoch += 1;
      refreshInFlight = null;
    });
  refreshInFlight = started;
  await started;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const status = error.response?.status;
    const original = error.config as
      | (InternalAxiosRequestConfig & RetriableConfig)
      | undefined;

    // --- upgrade_required: tenant lacks the feature → /pricing --------
    if (
      status === 403 &&
      error.response?.data?.error === "upgrade_required" &&
      typeof window !== "undefined"
    ) {
      window.location.assign("/pricing");
      return Promise.reject(error);
    }

    if (status !== 401 || !original) {
      return Promise.reject(error);
    }

    // --- totp_required: route the caller has RequireTOTPEnrolledAndVerified
    // and the current session JWT lacks the TOTP=true claim. This is NOT
    // an ordinary expiry — refreshing the access token won't add the
    // claim (issueSession on /refresh is called with stepUp=false), so
    // refresh-then-retry would loop. Short-circuit and let the caller
    // route to /2fa-verify. All strict-TOTP routes share this contract:
    // POSH ICC, complaints, offboarding /close, offboarding /compute-ff,
    // and any future RequireTOTPEnrolledAndVerified gate.
    if (error.response?.data?.error === "totp_required") {
      return Promise.reject(error);
    }

    // --- refresh_reuse: token theft detected, all sessions revoked. --
    // Backend 401s with { error: "refresh_reuse" } either on a normal
    // request or on the refresh POST itself. This is a security signal,
    // not an ordinary expiry — surface it instead of swallowing it.
    if (isRefreshReuse(error)) {
      redirectToLogin("session_revoked");
      return Promise.reject(error);
    }

    // The mount-time /me probe opts out of refresh+redirect: an
    // anonymous visitor just gets a quiet rejection (user=null).
    if (original._suppressAuthRedirect) {
      return Promise.reject(error);
    }

    // Never try to refresh the refresh call itself, and only retry
    // once. Both guards funnel through the idempotent redirect; we do
    // NOT assign here — redirectToLogin is the sole navigation owner,
    // so one logical failure can't double-redirect.
    const isRefreshCall = original.url?.includes(REFRESH_PATH);
    if (isRefreshCall || original._retried) {
      // A failed refresh POST re-arms the gate so a future,
      // legitimately new session can refresh again.
      if (isRefreshCall) {
        refreshInFlight = null;
        refreshEpoch += 1;
      }
      redirectToLogin();
      return Promise.reject(error);
    }

    // Epoch captured at the moment THIS request observed its 401 — the
    // refresh generation its now-stale cookie belongs to.
    const seenEpoch = refreshEpoch;
    original._retried = true;

    try {
      await coalescedRefresh(seenEpoch);
    } catch {
      redirectToLogin();
      return Promise.reject(error);
    }

    // Refresh succeeded (ours, a coalesced peer's, or one that landed
    // before we woke up). Replay exactly once. If the replay 401s,
    // _retried is set so we hit the guard above and redirect — we will
    // NOT rotate the refresh token a second time.
    return api(original);
  },
);

/**
 * Blob-response error rehydration.
 *
 * When a request uses `responseType: "blob"` (file/stream downloads),
 * axios decodes the response body as a Blob even on a 4xx/5xx. The
 * backend's JSON error envelope `{ error, message, field }` therefore
 * arrives as an UNPARSED Blob in `err.response.data`, so toApiError()
 * sees a non-object body and falls back to the generic
 * "Something went wrong" — masking specific branches like
 * not_found → "This document is not available." (which would otherwise
 * be dead code).
 *
 * This reads the Blob as text, best-effort JSON-parses it, and writes
 * the parsed envelope back onto `err.response.data` IN PLACE, then
 * returns the SAME axios error. Callers `await` this in the api layer
 * and re-reject the returned error so every downstream `toApiError(err)`
 * / `e.error === "not_found"` site keeps working unchanged.
 *
 * Security: this only ever surfaces the backend's own neutral envelope.
 * A forbidden/missing doc is 404 `{ error: "not_found" }` server-side
 * (no 403 existence oracle, no storage key on the wire); if the body is
 * not JSON or has no fields we leave a non-object data so toApiError()
 * yields the neutral generic — we never invent an existence signal.
 *
 * No-op (returns the input untouched) when the body is not a Blob, so
 * it is safe to wrap any download regardless of error shape.
 */
export async function blobErrorToApi(err: unknown): Promise<unknown> {
  if (!axios.isAxiosError(err)) return err;
  const ax = err as AxiosError<unknown>;
  const body = ax.response?.data;
  if (!(body instanceof Blob)) return err;
  try {
    const text = await body.text();
    const parsed: unknown = text ? JSON.parse(text) : null;
    if (parsed && typeof parsed === "object") {
      // Rehydrate in place so the existing toApiError() path (and the
      // not_found branch) reads it exactly as a normal JSON error.
      (ax.response as { data: unknown }).data = parsed;
    }
  } catch {
    // Non-JSON / unreadable body: leave data as-is. toApiError() will
    // fall through to the neutral generic message — no oracle.
  }
  return err;
}

/**
 * Normalize an axios error into the backend's { error, message, field }
 * envelope so callers (and react-hook-form wiring) get a stable shape.
 */
export function toApiError(err: unknown): ApiError {
  if (axios.isAxiosError(err)) {
    const data = (err as AxiosError<ApiError>).response?.data;
    if (data && typeof data === "object" && "message" in data) {
      return {
        error: data.error ?? "error",
        message: data.message ?? "Something went wrong",
        field: data.field || undefined,
      };
    }
    if (err.code === "ERR_NETWORK") {
      return {
        error: "network",
        message: "Cannot reach the server. Check your connection.",
      };
    }
  }
  return { error: "error", message: "Something went wrong" };
}

/**
 * Backend error code for the M-1 mandatory-2FA enrolment gate. 2FA is
 * mandatory for super_admin / hr_manager (CLAUDE.md locked decision);
 * the backend enforces it centrally in completeMembershipLogin
 * (auth.go) across login / activate / register / platform.
 */
export const TOTP_ENROLLMENT_REQUIRED = "totp_enrollment_required";

/**
 * True when a caught error is the M-1 mandatory-2FA enrolment gate:
 * HTTP 403 `{ error: "totp_enrollment_required" }`. The backend has
 * ALREADY set an enrolment-scoped IDENTITY-ONLY session cookie (no
 * tenant, no refresh, no CSRF row — the same fail-closed class as the
 * post-login picker), so the caller must route the user to /2fa-setup
 * to enrol a second factor before any tenant / privileged surface is
 * reachable. The 403 reaches callers intact — the response interceptor
 * only special-cases `upgrade_required` and 401 (see above).
 */
export function isTotpEnrollmentRequired(err: unknown): boolean {
  return toApiError(err).error === TOTP_ENROLLMENT_REQUIRED;
}
